/* eslint-disable max-len */
import { TournamentService } from '@/entities/tournament/api/service';
import { toStringDateAndTime } from '@/shared/lib/datetime/toStringDate';
import { Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styles from './TournamentBanner.module.scss';

const log = console.log.bind(console, '[Tournament Banner]:');

interface TournamentBannerProps {
	tournamentId?: number;
}

const TournamentBanner = ({ tournamentId }: TournamentBannerProps) => {
	const navigate = useNavigate();
	const { t } = useTranslation('translation', { keyPrefix: 'main' });
	const { t: tBanner } = useTranslation('translation', { keyPrefix: 'mainPage.banner.tournament' });

	const { data: tournament } = useQuery({
		enabled: !!tournamentId,
		queryFn: async () => await TournamentService.getActiveTournamentById(tournamentId),
		queryKey: [TournamentService.getActiveTournamentById.queryKey, tournamentId],
	});

	const startAtFormatted = useMemo(() => {
		if (!tournament || !tournament.startAt) return;

		log(`Converting tournament ${tournamentId} start at to string ${tournament.startAt}`);

		return toStringDateAndTime(new Date(tournament?.startAt));
	}, [tournament]);

	const endAtFormatted = useMemo(() => {
		if (!tournament || !tournament.endAt) return;

		log(`Converting tournament ${tournamentId} end at to string ${tournament.endAt}`);

		return toStringDateAndTime(new Date(tournament?.endAt));
	}, [tournament]);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.3 }}
			className={styles.tournamentBanner}
			onClick={() => {
				if (tournament) {
					navigate(`/tournament-overview/${tournament.id}`);

					return;
				}

				navigate('/tournament-selection');
			}}
		>
			<div className={styles.tournamentText}>
				<Typography variant='caption1' color='white' textTransform='uppercase' fontWeight={500}>
					{tournament?.name || t('join')}
				</Typography>
				<Typography variant='footnote' color='white' fontWeight={600}>
					{tournament
						? (() => {
								if (tournament.type === 'participants') {
									return tBanner('waitingForParticipants', { count: tournament.maxParticipants });
								}

								if (startAtFormatted && endAtFormatted) {
									return `${startAtFormatted} - ${endAtFormatted}`;
								}
						  })()
						: t('theTournament')}
				</Typography>
				<Typography variant='body3' className={styles.bannerPrizeText} fontWeight={600}>
					{tournament?.prizeFund
						? `${tournament?.prizeFund.toLocaleString('en-US')}.00 ${tournament.currency}`
						: `${t('prizePool')} 300K $`}
				</Typography>
			</div>
		</motion.div>
	);
};

export default TournamentBanner;
